import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SavedTreeItem, VariableType } from '@selfai-platform/pipeline-common';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';
import { TreeSelectModule } from 'primeng/treeselect';
import { NewTreeItem, VariableRowState } from '../../models';
import {
  CodeEditorManagerService,
  VariablesActionsService,
  VariablesEditingStateService,
  VariablesFormService,
  VariablesTreeDataService,
} from '../../services';
import { WorkflowVariablesStore } from '../../store';
import { isNewItem, isVariable, isVariableType } from '../../utils';

@Component({
  selector: 'selfai-platform-variables-edit-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TreeSelectModule,
    ButtonModule,
    TooltipModule,
    InputTextModule,
    InputTextareaModule,
    TranslateModule,
    InputGroupModule,
    InputGroupAddonModule,
  ],
  providers: [CodeEditorManagerService],
  templateUrl: './variables-edit-form.component.html',
  styleUrls: ['./variables-edit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariablesEditFormComponent implements OnDestroy {
  protected readonly store = inject(WorkflowVariablesStore);
  protected readonly treeDataService = inject(VariablesTreeDataService);
  private readonly editingStateService = inject(VariablesEditingStateService);
  private readonly formService = inject(VariablesFormService);
  private readonly actionsService = inject(VariablesActionsService);
  private readonly codeEditorManager = inject(CodeEditorManagerService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly nameInput = viewChild<ElementRef>('nameInput');
  private readonly codeEditorContainer = viewChild<ViewContainerRef, ViewContainerRef>('codeEditorContainer', {
    read: ViewContainerRef,
  });

  rowData = input.required<NewTreeItem>();
  editingMode = input.required<VariableRowState.EDITING | VariableRowState.EDITING_VALUE_ONLY>();

  protected readonly VariableRowState = VariableRowState;
  protected readonly editForm = this.formService.getForm();

  constructor() {
    effect(() => {
      const data = this.rowData();
      if (data && !isNewItem(data)) {
        this.initializeFormWithData(data);
        this.formService.setFieldsDisabledState(this.editingMode());
      }
    });

    afterNextRender(() => {
      if (this.editingStateService.isEditing()) {
        this.focusNameInput();
      }

      this.editForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.store.lastError() && this.editForm.dirty) {
          this.store.clearError();
        }
      });
    });
  }

  ngOnDestroy() {
    this.codeEditorManager.destroyCodeEditor();
  }

  protected saveItem(): void {
    this.actionsService.saveItem();
  }

  protected cancelEdit(): void {
    this.actionsService.cancelEdit();
    this.codeEditorManager.destroyCodeEditor();
  }

  protected openCodeEditor(control: FormControl): void {
    this.codeEditorManager.openCodeEditor(this.codeEditorContainer(), control);
  }

  protected getAbsolutePath(): string {
    return this.formService.getAbsolutePath();
  }

  protected copyAbsolutePath(): void {
    const absolutePath = this.getAbsolutePath();
    this.actionsService.copyAbsolutePath(absolutePath);
  }

  protected getUIGroupPath(groupId: string): string {
    return this.treeDataService.getUIGroupPath(groupId);
  }

  protected stopArrowPropagation(event: KeyboardEvent): void {
    if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.code)) {
      event.stopPropagation();
    }
  }

  protected getNodeIcon(): string {
    return this.isVariableType() ? 'pi pi-file' : 'pi pi-folder';
  }

  protected isVariableType(): boolean {
    return isVariableType(this.rowData());
  }

  protected isNewItem(): boolean {
    return isNewItem(this.rowData());
  }

  protected isEditingValueOnly(): boolean {
    return this.editingMode() === VariableRowState.EDITING_VALUE_ONLY;
  }

  private initializeFormWithData(data: SavedTreeItem): void {
    this.formService.patchFormValue({
      key: data.key,
      value: isVariable(data) ? data.value : null,
      description: data.description || '',
      regexRule: isVariable(data) ? data.regexRule || '' : null,
      parentGroup: this.treeDataService.findGroupNodeByKey(data.parentGroupId || ''),
    });

    this.formService.setValidators(isVariable(data) ? VariableType.VARIABLE : VariableType.GROUP);
    this.formService.setFieldsDisabledState(this.editingMode());
  }

  private focusNameInput(): void {
    setTimeout(() => {
      const inputElement = this.nameInput()?.nativeElement;
      if (inputElement) {
        inputElement.focus();
      }
    }, 0);
  }
}
